import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
    const navigate = useNavigate();

    return (
        <div className="bg-dark-bg text-white min-h-screen flex flex-col antialiased">
            <Header variant="landing" />

            <main className="flex-grow px-6 py-20">
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <div className="mb-12">
                        <button
                            onClick={() => navigate(-1)}
                            className="text-muted-gray hover:text-white transition-colors mb-6 flex items-center gap-2"
                        >
                            <span className="material-symbols-outlined text-sm">arrow_back</span>
                            <span className="text-sm">Back</span>
                        </button>
                        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Privacy Policy</h1>
                        <p className="text-muted-gray text-sm">Last updated: February 4, 2025</p>
                    </div>

                    {/* Content */}
                    <div className="space-y-8 text-gray-300 leading-relaxed">
                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Introduction</h2>
                            <p>
                                At Projekto, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose,
                                and safeguard your information when you use our project development services.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Information We Collect</h2>
                            <h3 className="text-white text-lg font-semibold mb-2 mt-4">Personal Information</h3>
                            <p className="mb-3">When you submit a project request, we collect:</p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Full name</li>
                                <li>Email address</li>
                                <li>Phone number</li>
                                <li>Project details and requirements</li>
                                <li>Payment information (processed securely through Razorpay)</li>
                            </ul>

                            <h3 className="text-white text-lg font-semibold mb-2 mt-4">Automatically Collected Information</h3>
                            <p className="mb-3">We may automatically collect:</p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>IP address and browser type</li>
                                <li>Device information</li>
                                <li>Usage data and analytics</li>
                                <li>Cookies and similar tracking technologies</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">How We Use Your Information</h2>
                            <p className="mb-3">We use your information to:</p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Process and fulfill your project requests</li>
                                <li>Communicate with you about your project status and updates</li>
                                <li>Process payments securely</li>
                                <li>Improve our services and user experience</li>
                                <li>Send important notifications and confirmations</li>
                                <li>Comply with legal obligations</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Information Sharing</h2>
                            <p className="mb-3">We do not sell your personal information. We may share your information with:</p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li><strong>Service Providers:</strong> Payment processors (Razorpay), hosting services, and analytics providers</li>
                                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                                <li><strong>Business Transfers:</strong> In connection with a merger, sale, or acquisition</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Data Security</h2>
                            <p>
                                We implement industry-standard security measures to protect your information. All payment transactions
                                are processed through Razorpay's secure payment gateway. However, no method of transmission over the
                                internet is 100% secure, and we cannot guarantee absolute security.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Your Rights</h2>
                            <p className="mb-3">You have the right to:</p>
                            <ul className="list-disc list-inside space-y-2 ml-4">
                                <li>Access the personal information we hold about you</li>
                                <li>Request correction of inaccurate information</li>
                                <li>Request deletion of your information (subject to legal obligations)</li>
                                <li>Opt-out of marketing communications</li>
                                <li>Withdraw consent where applicable</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Cookies</h2>
                            <p>
                                We use cookies and similar technologies to enhance your experience, analyze usage, and improve our services.
                                You can control cookie preferences through your browser settings.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Data Retention</h2>
                            <p>
                                We retain your personal information for as long as necessary to fulfill the purposes outlined in this
                                Privacy Policy, unless a longer retention period is required by law.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Children's Privacy</h2>
                            <p>
                                Our services are not intended for individuals under 18 years of age. We do not knowingly collect
                                information from children.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-white text-2xl font-bold mb-4">Changes to This Policy</h2>
                            <p>
                                We may update this Privacy Policy from time to time. We will notify you of any changes by posting
                                the new policy on this page and updating the "Last updated" date.
                            </p>
                        </section>

                        <section className="border-t border-thin-divider pt-8">
                            <h2 className="text-white text-2xl font-bold mb-4">Contact Us</h2>
                            <p className="mb-3">
                                If you have questions about this Privacy Policy or our data practices, please contact us:
                            </p>
                            <div className="bg-dark-card border border-border-gray rounded-xl p-6">
                                <p className="text-white font-semibold mb-2">Projekto Support</p>
                                <a href="mailto:projecktocare@gmail.com" className="text-blue-400 hover:text-blue-300 transition-colors">
                                    projecktocare@gmail.com
                                </a>
                            </div>
                        </section>
                    </div>
                </div>
            </main>

            <Footer variant="landing" />
        </div>
    );
};

export default PrivacyPolicy;
